/**
 * EmailThreadMessageCount — "N messages" for the thread header's meta row.
 *
 * Rendered inside `EmailThreadConversation`'s meta row (the "N emails" +
 * Expand all strip), not beside the sub-header's visibility badge / participant
 * stack. Data comes from the shared thread-timeline hook — one query and one
 * page-until-exhausted path across every thread surface, so the count can't
 * under-report a multi-page thread regardless of which other surfaces are
 * mounted.
 *
 * Shows a same-sized pulse bar during the initial load so the number doesn't
 * pop into an empty slot; renders nothing for a thread with no messages.
 */

import type { CrmObject } from '@/types/crm';

import { useThreadTimeline } from '../timeline/useThreadTimeline';

export function EmailThreadMessageCount({ object }: { object: CrmObject }) {
  const { messages, isLoading, isFetchingMore } = useThreadTimeline({
    entityId: object.entity_id,
    orgId: object.org_id,
  });

  const count = messages.length;

  if (count === 0) {
    if (isLoading || isFetchingMore) {
      return (
        <div className='flex h-4 items-center'>
          <div className='h-3 w-16 rounded bg-bg-tertiary animate-pulse' />
        </div>
      );
    }
    return null;
  }

  return (
    <span className='text-xs text-[var(--color-text-tertiary)]'>
      {count} {count === 1 ? 'email' : 'emails'}
    </span>
  );
}
